import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

const EnvelopeIntro = ({ isOpen, onOpen }) => {
  const [sealClicked, setSealClicked] = useState(false);

  const handleSealClick = () => {
    setSealClicked(true);
    setTimeout(() => {
      onOpen();
    }, 800); // Transición más corta
  };

  return (
    <AnimatePresence>
      {!isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#F9F7F2] overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="envelope-intro"
        >
          {/* Full screen envelope container */}
          <div className="relative w-full h-full flex items-center justify-center">
            
            {/* Envelope Back - Full Screen */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#F5F0EB] to-[#E8E2DB]" />

            {/* Top Flap - Opens upward with more dramatic animation */}
            <motion.div 
              className="absolute top-0 left-0 right-0 h-[50vh] origin-top z-[15]"
              style={{
                background: 'linear-gradient(180deg, #E8E2DB 0%, #DED8D0 100%)',
                clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                boxShadow: '0 4px 20px rgba(62, 39, 35, 0.1)',
              }}
              animate={sealClicked ? { 
                rotateX: -180,
                y: '-10%',
                transition: { duration: 0.6, ease: [0.6, 0, 0.2, 1] }
              } : {}}
            />

            {/* Bottom Flap - slides down when opening */}
            <motion.div 
              className="absolute bottom-0 left-0 right-0 h-[55vh] md:h-[50vh] z-[10]"
              style={{
                background: 'linear-gradient(0deg, #F5F0EB 0%, #EBE5DD 100%)',
                clipPath: 'polygon(0 100%, 50% 20%, 100% 100%)',
              }}
              animate={sealClicked ? {
                y: '20%',
                opacity: 0,
                transition: { duration: 0.5, ease: 'easeOut', delay: 0.1 }
              } : {}}
            />

            {/* Left Flap - slides left when opening */}
            <motion.div 
              className="absolute top-0 bottom-0 left-0 w-[50vw] z-[5]"
              style={{
                background: 'linear-gradient(90deg, #EFEBE5 0%, #F5F0EB 100%)',
                clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
              }}
              animate={sealClicked ? {
                x: '-30%',
                opacity: 0,
                transition: { duration: 0.5, ease: 'easeOut', delay: 0.05 }
              } : {}}
            />

            {/* Right Flap - slides right when opening */}
            <motion.div 
              className="absolute top-0 bottom-0 right-0 w-[50vw] z-[5]"
              style={{
                background: 'linear-gradient(-90deg, #EFEBE5 0%, #F5F0EB 100%)',
                clipPath: 'polygon(100% 0, 0 50%, 100% 100%)',
              }}
              animate={sealClicked ? {
                x: '30%',
                opacity: 0,
                transition: { duration: 0.5, ease: 'easeOut', delay: 0.05 }
              } : {}}
            />

            {/* Decorative lines on envelope */}
            <div className="absolute top-[25%] left-[25%] w-[50%] h-[1px] bg-[#D7CCC8] opacity-30 z-[3]" />
            <div className="absolute top-[30%] left-[30%] w-[40%] h-[1px] bg-[#D7CCC8] opacity-20 z-[3]" />

            {/* Wax Seal - Center of screen */}
            <AnimatePresence>
              {!sealClicked && (
                <motion.button
                  className="absolute z-[30] w-24 h-24 md:w-32 md:h-32 rounded-full cursor-pointer
                             flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #9D5D4C 0%, #6D4C41 40%, #5D4037 70%, #4A3228 100%)',
                    boxShadow: `
                      0 10px 40px rgba(62, 39, 35, 0.5),
                      0 6px 20px rgba(62, 39, 35, 0.4),
                      inset 0 3px 6px rgba(255, 255, 255, 0.2),
                      inset 0 -4px 8px rgba(0, 0, 0, 0.3)
                    `,
                  }}
                  onClick={handleSealClick}
                  exit={{ 
                    scale: 1.5,
                    opacity: 0,
                    transition: { duration: 0.3, ease: 'easeOut' }
                  }}
                  data-testid="wax-seal-button"
                  aria-label="Abrir invitación"
                >
                  {/* Seal texture rings */}
                  <div 
                    className="absolute inset-3 rounded-full"
                    style={{
                      border: '2px solid rgba(255,255,255,0.15)',
                    }}
                  />
                  <div 
                    className="absolute inset-5 rounded-full"
                    style={{
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  />
                  
                  {/* Initials */}
                  <span 
                    className="font-heading text-[#F5F0EB] text-2xl md:text-3xl font-medium tracking-wider"
                    style={{
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
                    }}
                  >
                    C&nbsp;|&nbsp;L
                  </span>
                </motion.button>
              )}
            </AnimatePresence>

            {/* Instruction text - CENTERED */}
            <AnimatePresence>
              {!sealClicked && (
                <motion.p
                  className="absolute bottom-[15%] left-0 right-0 text-center
                             font-body text-xs md:text-sm text-[#8D6E63] tracking-[0.2em] uppercase z-[25]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                >
                  Toca el sello para abrir
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnvelopeIntro;
